/* eslint-disable react-refresh/only-export-components */
/**
 * One row of the activity feed.
 *
 * Numbers and dates go through lib/format so every screen reads the same;
 * the few helpers defined here are the ones only the event views need, and
 * Feed.tsx / Product.tsx / StockTimeline import them from here.
 */
import { Link } from "react-router-dom";
import { money, relativeTime, resolveImage, shortDate } from "../lib/format";
import type { EventType, WatchEvent } from "../lib/types";

/** Event from/to values arrive as strings; price events carry raw cents. */
export function formatValue(type: EventType, value?: string): string {
  if (value === undefined || value === "") return "—";
  if (type === "price-change" && /^\d+$/.test(value)) return money(Number(value));
  return value;
}

/** Date plus time of day, for the tooltips that sit behind a relative time. */
export function fullStamp(iso?: string): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  const time = date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  return `${shortDate(iso)} · ${time}`;
}

/** Day bucket used by the feed's date markers. */
export function formatDay(iso?: string): string {
  if (!iso) return "UNDATED";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "UNDATED";
  const today = new Date();
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (sameDay(date, today)) return "TODAY";
  if (sameDay(date, new Date(today.getTime() - 86_400_000))) return "YESTERDAY";
  return shortDate(iso);
}

/* ------------------------------------------------------------------ */
/* event vocabulary                                                    */
/* ------------------------------------------------------------------ */

/** Short chip label per event type. Record<> keeps this exhaustive. */
export const EVENT_LABEL: Record<EventType, string> = {
  "product-new": "NEW DROP",
  "product-restock": "RESTOCK",
  "product-sold-out": "SOLD OUT",
  "product-gone": "REMOVED",
  "product-returned": "RETURNED",
  "variant-restock": "SIZE BACK",
  "variant-sold-out": "SIZE OUT",
  "variant-new": "SIZE ADDED",
  "price-change": "PRICE",
  "title-change": "RENAMED",
  "page-new": "PAGE NEW",
  "page-gone": "PAGE GONE",
  "page-composition-change": "PAGE EDIT",
  "unlisted-product": "UNLISTED",
  "feed-mismatch": "MISMATCH",
};

export const EVENT_TYPES = Object.keys(EVENT_LABEL) as EventType[];

export function eventLabel(type: EventType | string): string {
  return EVENT_LABEL[type as EventType] ?? String(type).replace(/-/g, " ").toUpperCase();
}

function describe(event: WatchEvent): string | null {
  if (event.detail) return event.detail;
  if (event.fromValue !== undefined && event.toValue !== undefined) {
    return `${formatValue(event.type, event.fromValue)} → ${formatValue(event.type, event.toValue)}`;
  }
  if (event.path) return event.path;
  return null;
}

/* ------------------------------------------------------------------ */
/* styles                                                              */
/* ------------------------------------------------------------------ */

const CSS = `
.awev-row{
  --ink:#0D0D0D; --red:#FF0000; --muted:#8A8A8A; --line:rgba(13,13,13,.14);
  list-style:none;
  display:grid;
  grid-template-columns:3px 96px minmax(0,1fr) auto;
  column-gap:14px;
  align-items:start;
  padding:13px 16px 13px 0;
  background:#FFFFFF;
  border-bottom:1px solid var(--line);
}
.awev-row:last-child{border-bottom:none}
.awev-rail{grid-column:1;align-self:stretch;background:#D6D6D6}
.awev-notable .awev-rail{background:var(--ink)}
.awev-alert .awev-rail{background:var(--red)}

.awev-chip{
  grid-column:2;
  justify-self:start;
  margin-top:1px;
  padding:4px 6px;
  border:1px solid #CFCFCF;
  color:var(--muted);
  font-family:"Oswald","Arial Narrow","Helvetica Neue",sans-serif;
  font-size:10px;
  line-height:1;
  letter-spacing:.14em;
  text-transform:uppercase;
  white-space:nowrap;
}
.awev-notable .awev-chip{border-color:var(--ink);color:var(--ink)}
.awev-alert .awev-chip{border-color:var(--red);color:var(--red)}

.awev-body{grid-column:3;display:flex;gap:11px;min-width:0}
.awev-thumb{
  width:38px;height:38px;flex:0 0 38px;
  object-fit:cover;background:#EDEDED;border:1px solid var(--line);
}
.awev-text{min-width:0;flex:1 1 auto}
.awev-head{display:flex;align-items:baseline;gap:8px;flex-wrap:wrap}
.awev-title{
  font-family:"Anton","Arial Narrow","Helvetica Neue",Impact,sans-serif;
  font-size:15px;line-height:1.15;letter-spacing:.02em;
  text-transform:uppercase;color:var(--ink);text-decoration:none;
  overflow-wrap:anywhere;
}
a.awev-title:hover{text-decoration:underline;text-underline-offset:3px}
.awev-alert a.awev-title,.awev-alert .awev-title{color:var(--ink)}
.awev-size{
  font-family:"Oswald","Arial Narrow",sans-serif;
  font-size:10px;line-height:1;letter-spacing:.12em;
  border:1px solid var(--ink);padding:3px 5px;white-space:nowrap;
}
.awev-detail{
  margin:5px 0 0;
  font-family:"Sora","Helvetica Neue",Arial,sans-serif;
  font-size:11px;line-height:1.55;color:var(--muted);
  overflow-wrap:anywhere;
}
.awev-alert .awev-detail{color:#8C1D1D}
.awev-time{
  grid-column:4;
  padding-top:3px;
  font-family:"Sora","Helvetica Neue",Arial,sans-serif;
  font-size:10px;letter-spacing:.06em;text-transform:uppercase;
  color:var(--muted);white-space:nowrap;
}

@media (max-width:640px){
  .awev-row{grid-template-columns:3px minmax(0,1fr);column-gap:12px;row-gap:7px;padding-right:12px}
  .awev-rail{grid-row:1 / span 3}
  .awev-chip{grid-column:2;grid-row:1}
  .awev-body{grid-column:2;grid-row:2}
  .awev-time{grid-column:2;grid-row:3;padding-top:0}
}
`;

const STYLE_ID = "aw-event-row-styles";
if (typeof document !== "undefined" && !document.getElementById(STYLE_ID)) {
  const tag = document.createElement("style");
  tag.id = STYLE_ID;
  tag.textContent = CSS;
  document.head.appendChild(tag);
}

/* ------------------------------------------------------------------ */
/* component                                                           */
/* ------------------------------------------------------------------ */

export interface EventRowProps {
  event: WatchEvent;
}

export function EventRow({ event }: EventRowProps) {
  const detail = describe(event);
  const heading = event.title ?? event.handle ?? event.path ?? "UNTITLED";
  const thumb = resolveImage(event.imageUrl);

  const title =
    event.productId !== undefined ? (
      <Link className="awev-title" to={`/p/${event.productId}`}>
        {heading}
      </Link>
    ) : (
      <span className="awev-title">{heading}</span>
    );

  return (
    <li className={`awev-row awev-${event.severity}`}>
      <span className="awev-rail" aria-hidden="true" />
      <span className="awev-chip">{eventLabel(event.type)}</span>

      <div className="awev-body">
        {thumb ? <img className="awev-thumb" src={thumb} alt="" loading="lazy" /> : null}
        <div className="awev-text">
          <div className="awev-head">
            {title}
            {event.size ? <span className="awev-size">{event.size}</span> : null}
          </div>
          {detail ? <p className="awev-detail">{detail}</p> : null}
        </div>
      </div>

      <time className="awev-time" dateTime={event.occurredAt} title={fullStamp(event.occurredAt)}>
        {relativeTime(event.occurredAt)}
      </time>
    </li>
  );
}

export default EventRow;
